<template>
	<div
		class="chat-toast"
		:class="`chat-toast--${toast.kind}`"
		:style="accentStyle"
		:role="toast.kind === 'error' ? 'alert' : 'status'"
	>
		<channel-avatar v-if="toast.typeId" :type-id="toast.typeId" :image="toast.image" :size="36" badge />
		<chat-icon v-else :name="toast.kind === 'error' ? 'alert' : 'chat'" :size="22" class="chat-toast__icon" />
		<div class="chat-toast__body">
			<p v-if="toast.title" class="chat-toast__title">{{ toast.title }}</p>
			<p v-if="toast.subtitle" class="chat-toast__subtitle">{{ toast.subtitle }}</p>
			<p class="chat-toast__text">{{ toast.text }}</p>
			<button v-if="toast.target" type="button" class="chat-toast__action" @click="$emit('open', toast)">
				{{ $t('chat.open') }}
			</button>
		</div>
		<button type="button" class="chat-toast__close" :aria-label="$t('chat.close')" @click="$emit('dismiss', toast)">
			<chat-icon name="close" :size="16" />
		</button>
	</div>
</template>

<script>
	import ChannelAvatar from './ChannelAvatar.vue';
	import ChatIcon from './ChatIcon.vue';
	import { chatChannelVisual } from './chatVisuals';

	const KIND_ACCENTS = { info: 'var(--info)', error: 'var(--primary)' };

	export default {
		name: 'ChatToast',
		components: { ChannelAvatar, ChatIcon },
		props: {
			toast: {
				type: Object,
				required: true,
			},
		},
		emits: ['open', 'dismiss'],
		computed: {
			accentStyle() {
				const accent = this.toast.typeId
					? chatChannelVisual(this.toast.typeId).accent
					: KIND_ACCENTS[this.toast.kind] || KIND_ACCENTS.info;
				return { '--chat-toast-accent': accent };
			},
		},
	};
</script>

<style lang="scss" scoped>
	.chat-toast {
		display: flex;
		align-items: flex-start;
		gap: 10px;
		width: 320px;
		max-width: calc(100vw - 32px);
		padding: 10px 8px 10px 12px;
		color: #1a202c;
		background: var(--white);
		border-left: 4px solid var(--chat-toast-accent);
		border-radius: 8px;
		box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
		pointer-events: auto;
	}

	.chat-toast__icon {
		margin-top: 1px;
		color: var(--chat-toast-accent);
	}

	.chat-toast__body {
		flex: 1;
		min-width: 0;
	}

	.chat-toast__title {
		overflow: hidden;
		font-size: 13px;
		font-weight: 700;
		line-height: 1.3;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.chat-toast__subtitle {
		overflow: hidden;
		font-size: 10px;
		font-weight: 700;
		line-height: 1.4;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		white-space: nowrap;
		text-overflow: ellipsis;
		color: var(--chat-toast-accent);
	}

	.chat-toast__text {
		display: -webkit-box;
		margin-top: 2px;
		overflow: hidden;
		font-size: 13px;
		line-height: 1.35;
		color: #4a5568;
		word-break: break-word;
		white-space: pre-line;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
	}

	.chat-toast--error .chat-toast__text {
		color: #1a202c;
	}

	.chat-toast__action {
		margin-top: 6px;
		padding: 3px 12px;
		font-size: 12px;
		font-weight: 700;
		color: var(--white);
		background: var(--chat-toast-accent);
		border: 0;
		border-radius: 9999px;
		cursor: pointer;
	}

	.chat-toast__close {
		display: flex;
		padding: 2px;
		color: #718096;
		background: transparent;
		border: 0;
		border-radius: 9999px;
		cursor: pointer;

		&:hover {
			color: #1a202c;
		}
	}
</style>
